import { useMemo } from 'react'
import { Select, Space, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { costCenterDepths, fetchCostCenters } from './financeApi'

const INDENT = '  '

export interface CostCenterFilterProps {
  value: string | null
  onChange: (nodeId: string | null) => void
}

export function CostCenterFilter({ value, onChange }: CostCenterFilterProps) {
  const { t } = useTranslation()

  const centers = useQuery({
    queryKey: queryKeys.finance.costCenters,
    queryFn: ({ signal }) => fetchCostCenters(signal),
  })

  const options = useMemo(() => {
    const rows = centers.data ?? []
    const depths = costCenterDepths(rows)
    return rows.map((node) => ({
      value: node.id,
      label: `${INDENT.repeat(depths.get(node.id) ?? 0)}${node.displayName} · ${node.code}`,
    }))
  }, [centers.data])

  return (
    <Space wrap size="middle" align="center">
      <Typography.Text type="secondary">{t('finance.nodeFilter.label')}:</Typography.Text>
      <Select<string>
        allowClear
        showSearch
        optionFilterProp="label"
        value={value ?? undefined}
        onChange={(nodeId?: string) => onChange(nodeId ?? null)}
        options={options}
        loading={centers.isPending}
        status={centers.isError ? 'error' : undefined}
        placeholder={t('finance.nodeFilter.all')}
        notFoundContent={
          centers.isError ? translateError(centers.error, t) : t('finance.nodeFilter.empty')
        }
        style={{ width: 280 }}
      />
    </Space>
  )
}
